// The MARQUEE multi-selection domain (layout Select tool): the rubber-band
// gesture (replace / Shift-add / Ctrl-remove, with the screen-space hit
// testing), the selection sets themselves, the front-layer (non-passthrough)
// filter, and delete-selected. 2D selects walls + placements; 3D placements
// only. The group MOVE of a marquee selection (and the gizmo that acts on it)
// stays in floor-mode — it's the placement-move cluster; it reads the sets
// through this API.

import * as THREE from "three";
import { placementSystem, wallSystem } from "../core";
import { uiState } from "../../ui/state";
import { desktopMouse } from "../../input/desktop";

export type MarqueeMode = "replace" | "add" | "remove";

export interface MarqueeDeps {
  activeCamera: () => THREE.Camera;
  canvasRect: () => DOMRect;
  occluderDist: () => number; // the side-elevation pick barrier (floor-mode)
  refreshSelectionUI: () => void;
  commitFloor: () => void; // delete-selected is one undo step
  band: { show(x0: number, y0: number, x1: number, y1: number): void; hide(): void };
}

export interface MarqueeApi {
  walls: () => ReadonlySet<number>;
  places: () => ReadonlySet<number>;
  hasSelection: () => boolean;
  // Make the selection exactly these ids and highlight them (group selections
  // ride on the marquee set — see selectPlacementOrGroup / selectPlacements).
  setSelection: (walls: Iterable<number>, places: Iterable<number>) => void;
  clear: () => void;
  deleteSelected: () => void; // remove every selected wall + placement (one undo)
  pressHitsSelection: () => boolean; // press on an already-selected item → group move
  // The rubber-band gesture.
  setNonPass: (v: boolean) => void; // per-drag: select only the front layer
  setReplaceMode: () => void; // plain press (band starts later, at empty space)
  startBand: (clientX: number, clientY: number) => void;
  beginAdditiveBand: (mode: MarqueeMode, clientX: number, clientY: number) => void;
  bandActive: () => boolean;
  updateBand: () => void; // per-frame while held: draw the rect + recompute hits
  releaseBand: () => void; // click → clear / toggle-one; drag → keep the sweep
  cancelBand: () => void; // force-cancel: drop the band with no release semantics
  // Persistent front-layer-only toggle (the per-drag Alt modifier ORs with it).
  frontLayerOn: () => boolean;
  toggleFrontLayer: () => void;
}

type Rect = { minX: number; minY: number; maxX: number; maxY: number };

export function installMarquee(deps: MarqueeDeps): MarqueeApi {
  const marqWalls = new Set<number>();
  const marqPlaces = new Set<number>();
  let marqueeStart: { x: number; y: number } | null = null; // rubber-band origin (client px)
  // How the rubber-band combines with the existing selection: plain = replace,
  // Shift = add, Ctrl/Cmd = remove. `marqueeBase` is the selection at press time
  // that add/remove are applied against.
  let marqueeMode: MarqueeMode = "replace";
  let marqueeNonPass = false; // this drag: select only the front-most placements
  // Persistent non-passthrough toggle; the per-drag Alt modifier ORs with it.
  let frontLayerSelect = false;
  const marqueeBase = { walls: new Set<number>(), places: new Set<number>() };
  const _ray = new THREE.Raycaster();
  const _w2c = new THREE.Vector3();
  const _w2cView = new THREE.Vector3();

  function hasSelection(): boolean {
    return marqWalls.size > 0 || marqPlaces.size > 0;
  }
  function applyHighlight(): void {
    // Walls show via their highlight material; placements show via white mesh
    // edges (driven each frame in updateFloorGizmo) — no separate outline box.
    wallSystem.setHighlighted(marqWalls);
  }
  function clear(): void {
    if (!hasSelection()) return;
    marqWalls.clear();
    marqPlaces.clear();
    applyHighlight();
  }
  function worldToClient(x: number, y: number, z: number): { x: number; y: number } {
    _w2c.set(x, y, z).project(deps.activeCamera());
    const r = deps.canvasRect();
    return {
      x: (_w2c.x * 0.5 + 0.5) * r.width + r.left,
      y: (-_w2c.y * 0.5 + 0.5) * r.height + r.top,
    };
  }

  // ── Screen-space hit-test helpers (client px) ──────────────────────────────
  function ccw(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
    return (cy - ay) * (bx - ax) > (by - ay) * (cx - ax);
  }
  function segsCross(
    ax: number, ay: number, bx: number, by: number,
    cx: number, cy: number, dx: number, dy: number,
  ): boolean {
    return (
      ccw(ax, ay, cx, cy, dx, dy) !== ccw(bx, by, cx, cy, dx, dy) &&
      ccw(ax, ay, bx, by, cx, cy) !== ccw(ax, ay, bx, by, dx, dy)
    );
  }
  function ptInRect(x: number, y: number, r: Rect): boolean {
    return x >= r.minX && x <= r.maxX && y >= r.minY && y <= r.maxY;
  }
  // A segment "touches" the rect if an endpoint is inside or it crosses any edge.
  function segTouchesRect(ax: number, ay: number, bx: number, by: number, r: Rect): boolean {
    if (ptInRect(ax, ay, r) || ptInRect(bx, by, r)) return true;
    return (
      segsCross(ax, ay, bx, by, r.minX, r.minY, r.maxX, r.minY) ||
      segsCross(ax, ay, bx, by, r.maxX, r.minY, r.maxX, r.maxY) ||
      segsCross(ax, ay, bx, by, r.maxX, r.maxY, r.minX, r.maxY) ||
      segsCross(ax, ay, bx, by, r.minX, r.maxY, r.minX, r.minY)
    );
  }
  function rectsOverlap(a: Rect, b: Rect): boolean {
    return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
  }
  // Screen-space (client px) bounding box of a placement's world AABB corners.
  function placementScreenBox(id: number): Rect | null {
    const b = placementSystem.worldBox(id);
    if (!b) return null;
    const cam = deps.activeCamera();
    const r = deps.canvasRect();
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let anyFront = false;
    for (const cx of [b.minX, b.maxX]) {
      for (const cy of [b.minY, b.maxY]) {
        for (const cz of [b.minZ, b.maxZ]) {
          // View-space z < 0 means the corner is in FRONT of the camera (it
          // looks down -z). Behind-camera corners project with a wrap-around,
          // so skip them — an object entirely behind the camera isn't selectable.
          _w2cView.set(cx, cy, cz).applyMatrix4(cam.matrixWorldInverse);
          if (_w2cView.z >= 0) continue;
          anyFront = true;
          _w2c.set(cx, cy, cz).project(cam);
          const px = (_w2c.x * 0.5 + 0.5) * r.width + r.left;
          const py = (-_w2c.y * 0.5 + 0.5) * r.height + r.top;
          minX = Math.min(minX, px);
          minY = Math.min(minY, py);
          maxX = Math.max(maxX, px);
          maxY = Math.max(maxY, py);
        }
      }
    }
    return anyFront ? { minX, minY, maxX, maxY } : null;
  }

  // Non-passthrough filter for placements: drop any that's occluded by a CLOSER
  // candidate (whose screen box covers its centre), leaving only the visible
  // front layer. O(n²) but n (selected placements) is small.
  const _flCamPos = new THREE.Vector3();
  const _flCamFwd = new THREE.Vector3();
  const _flCenter = new THREE.Vector3();
  function frontLayerPlacements(hitP: Set<number>): void {
    const cam = deps.activeCamera();
    cam.getWorldPosition(_flCamPos);
    cam.getWorldDirection(_flCamFwd);
    const cands: { id: number; box: Rect; depth: number }[] = [];
    for (const id of hitP) {
      const box = placementScreenBox(id);
      const b = placementSystem.worldBox(id);
      if (!box || !b) continue;
      _flCenter
        .set((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, (b.minZ + b.maxZ) / 2)
        .sub(_flCamPos);
      cands.push({ id, box, depth: _flCenter.dot(_flCamFwd) }); // along camera forward
    }
    for (const c of cands) {
      const cx = (c.box.minX + c.box.maxX) / 2;
      const cy = (c.box.minY + c.box.maxY) / 2;
      for (const d of cands) {
        if (d.id === c.id || d.depth >= c.depth - 1e-4) continue; // only CLOSER ones
        if (cx >= d.box.minX && cx <= d.box.maxX && cy >= d.box.minY && cy <= d.box.maxY) {
          hitP.delete(c.id);
          break;
        }
      }
    }
  }

  // Recompute the selection from the rubber-band rect: anything the rect
  // TOUCHES is hit — placements whose screen box overlaps (2D + 3D), walls
  // whose centerline crosses it (2D only). Then combine with `marqueeBase`.
  function computeMarqueeSelection(x0: number, y0: number, x1: number, y1: number): void {
    const r: Rect = {
      minX: Math.min(x0, x1),
      minY: Math.min(y0, y1),
      maxX: Math.max(x0, x1),
      maxY: Math.max(y0, y1),
    };
    const hitW = new Set<number>();
    const hitP = new Set<number>();
    for (const id of placementSystem.ids()) {
      const box = placementScreenBox(id);
      if (box && rectsOverlap(box, r)) hitP.add(id);
    }
    // Non-passthrough: drop placements occluded by a closer one (the front layer).
    if (marqueeNonPass && hitP.size > 1) frontLayerPlacements(hitP);
    if (uiState.floorView === "2d") {
      for (const s of wallSystem.wallSegments()) {
        const a = worldToClient(s.ax, 0, s.az);
        const b = worldToClient(s.bx, 0, s.bz);
        if (segTouchesRect(a.x, a.y, b.x, b.y, r)) hitW.add(s.id);
      }
    }
    // Catching any member of a group catches the whole group, so it stays a unit.
    for (const id of [...hitP]) {
      for (const sib of placementSystem.groupSiblings(id)) hitP.add(sib);
    }
    combine(hitW, hitP);
  }
  // Apply a freshly-hit set to the base selection per the active mode.
  function combine(hitW: Set<number>, hitP: Set<number>): void {
    marqWalls.clear();
    marqPlaces.clear();
    if (marqueeMode === "add") {
      for (const id of marqueeBase.walls) marqWalls.add(id);
      for (const id of marqueeBase.places) marqPlaces.add(id);
      for (const id of hitW) marqWalls.add(id);
      for (const id of hitP) marqPlaces.add(id);
    } else if (marqueeMode === "remove") {
      for (const id of marqueeBase.walls) if (!hitW.has(id)) marqWalls.add(id);
      for (const id of marqueeBase.places) if (!hitP.has(id)) marqPlaces.add(id);
    } else {
      for (const id of hitW) marqWalls.add(id);
      for (const id of hitP) marqPlaces.add(id);
    }
    applyHighlight();
  }
  // Snapshot the current selection as the base for an add/remove marquee,
  // folding in any single (gizmo/handle) selection so modifiers extend from it.
  function beginBase(): void {
    marqueeBase.walls.clear();
    marqueeBase.places.clear();
    for (const id of marqWalls) marqueeBase.walls.add(id);
    for (const id of marqPlaces) marqueeBase.places.add(id);
    const w = wallSystem.selectedId();
    const p = placementSystem.selectedId();
    if (w !== null) {
      marqueeBase.walls.add(w);
      marqWalls.add(w);
    }
    if (p !== null) {
      marqueeBase.places.add(p);
      marqPlaces.add(p);
    }
    if (w !== null || p !== null) {
      wallSystem.setSelected(null);
      placementSystem.setSelected(null);
      deps.refreshSelectionUI();
    }
  }
  // The wall / placement directly under the cursor (2D walls only), or null —
  // for Shift/Ctrl CLICKS, which toggle a single item rather than sweep a box.
  function pickSelectItem(): { kind: "wall" | "place"; id: number } | null {
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    const ph = placementSystem.pickHit(_ray);
    const wh =
      uiState.floorView === "2d"
        ? _ray.intersectObjects(wallSystem.wallMeshes(), false)
        : [];
    const wallDist = wh.length > 0 ? wh[0].distance : Infinity;
    const barDist = deps.occluderDist(); // nothing past a door-edit barrier
    if (ph && ph.distance <= wallDist && ph.distance <= barDist) return { kind: "place", id: ph.id };
    if (wh.length > 0 && wallDist <= barDist) {
      const wid = wallSystem.wallIdForMesh(wh[0].object);
      if (wid !== null) return { kind: "wall", id: wid };
    }
    return null;
  }
  // True when the press ray hits an item already selected (→ group move).
  function pressHitsSelection(): boolean {
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    const ph = placementSystem.pickHit(_ray);
    if (ph && marqPlaces.has(ph.id)) return true;
    if (uiState.floorView === "2d") {
      const wh = _ray.intersectObjects(wallSystem.wallMeshes(), false);
      if (wh.length > 0) {
        const wid = wallSystem.wallIdForMesh(wh[0].object);
        if (wid !== null && marqWalls.has(wid)) return true;
      }
    }
    return false;
  }
  // Remove every marquee-selected wall + placement in one undo step.
  function deleteSelected(): void {
    if (!hasSelection()) return;
    for (const id of marqPlaces) placementSystem.remove(id);
    for (const id of marqWalls) wallSystem.removeWall(id);
    marqWalls.clear();
    marqPlaces.clear();
    applyHighlight();
    deps.refreshSelectionUI();
    deps.commitFloor();
  }

  // ── The rubber-band gesture ─────────────────────────────────────────────
  function updateBand(): void {
    if (marqueeStart === null || !desktopMouse.lmb) return;
    deps.band.show(marqueeStart.x, marqueeStart.y, desktopMouse.clientX, desktopMouse.clientY);
    computeMarqueeSelection(
      marqueeStart.x,
      marqueeStart.y,
      desktopMouse.clientX,
      desktopMouse.clientY,
    );
  }
  function releaseBand(): void {
    if (marqueeStart === null) return;
    const moved =
      Math.hypot(
        desktopMouse.clientX - marqueeStart.x,
        desktopMouse.clientY - marqueeStart.y,
      ) > 3;
    deps.band.hide();
    if (!moved) {
      if (marqueeMode === "replace") {
        clear(); // plain empty-space click → drop the selection
      } else {
        // Shift/Ctrl CLICK → toggle just the item under the cursor against the
        // base (robust for thin walls a point-rect wouldn't cross).
        const hit = pickSelectItem();
        const hitW = new Set<number>();
        const hitP = new Set<number>();
        if (hit) (hit.kind === "wall" ? hitW : hitP).add(hit.id);
        combine(hitW, hitP);
      }
    }
    marqueeStart = null;
  }

  return {
    walls: () => marqWalls,
    places: () => marqPlaces,
    hasSelection,
    setSelection: (ws, ps) => {
      marqWalls.clear();
      marqPlaces.clear();
      for (const id of ws) marqWalls.add(id);
      for (const id of ps) marqPlaces.add(id);
      applyHighlight();
    },
    clear,
    deleteSelected,
    pressHitsSelection,
    setNonPass: (v) => {
      marqueeNonPass = v;
    },
    setReplaceMode: () => {
      marqueeMode = "replace";
    },
    startBand: (x, y) => {
      marqueeStart = { x, y };
    },
    beginAdditiveBand: (mode, x, y) => {
      marqueeMode = mode;
      beginBase();
      applyHighlight();
      marqueeStart = { x, y };
    },
    bandActive: () => marqueeStart !== null,
    updateBand,
    releaseBand,
    cancelBand: () => {
      marqueeStart = null;
    },
    frontLayerOn: () => frontLayerSelect,
    toggleFrontLayer: () => {
      frontLayerSelect = !frontLayerSelect;
    },
  };
}
